import React from "react";
import axios from "axios";
import { format } from "date-fns";
import { es as esLocale } from "date-fns/locale";
import { Calendar as CalendarIcon, CheckCircle2 } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { DEALER, VEHICLES, TIME_SLOTS } from "@/data/dealership";
import { useT } from "@/i18n/lang";
import FinancingCTA from "@/components/FinancingCTA";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Appointment() {
  const { t, lang } = useT();
  const [form, setForm] = React.useState({
    name: "", email: "", phone: "", vehicle_interest: "", message: "",
  });
  const [date, setDate] = React.useState(null);
  const [time, setTime] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [success, setSuccess] = React.useState(false);

  const dateLocale = lang === "es" ? esLocale : undefined;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !date || !time) {
      toast.error(t("appt.fillAll"));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/appointments`, {
        ...form,
        preferred_date: format(date, "yyyy-MM-dd"),
        preferred_time: time,
      });
      setSuccess(true);
      toast.success(t("appt.success"));
      setForm({ name: "", email: "", phone: "", vehicle_interest: "", message: "" });
      setDate(null);
      setTime("");
    } catch (err) {
      toast.error(`${t("appt.error")} ${DEALER.phone}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="appointment" data-testid="appointment-section" className="px-6 md:px-12 lg:px-16 py-20 md:py-32 bg-[#0A0A0A] text-white">
      <div className="max-w-[1440px] mx-auto grid grid-cols-12 gap-8">
        <div className="col-span-12 lg:col-span-5">
          <div className="flex items-center gap-3 mb-4">
            <span className="w-8 h-px bg-[#EB0A1E]" />
            <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#EB0A1E]">
              {t("appt.eyebrow")}
            </span>
          </div>
          <h2 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
            {t("appt.h1a")}<br />{t("appt.h1b")}
          </h2>
          <p className="mt-6 text-white/70 max-w-md text-base md:text-lg leading-relaxed">
            {t("appt.lede")}
          </p>

          <div className="mt-10 space-y-4">
            <Bullet>{t("appt.bullet1")}</Bullet>
            <Bullet>{t("appt.bullet2")}</Bullet>
            <Bullet>{t("appt.bullet3")}</Bullet>
          </div>

          <a
            href={`tel:${DEALER.phoneRaw}`}
            data-testid="appt-direct-call"
            className="mt-10 inline-flex items-center gap-3 text-white border-b-2 border-[#EB0A1E] pb-1 hover:text-[#EB0A1E] transition-colors"
          >
            {t("appt.direct")} {DEALER.phone}
          </a>

          <div className="mt-4">
            <FinancingCTA page="appointment" testid="appt-finance-btn">
              <button
                type="button"
                className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm underline underline-offset-4 decoration-[#EB0A1E] decoration-2 transition-colors"
              >
                {t("appt.financeCta")} →
              </button>
            </FinancingCTA>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-7">
          {success ? (
            <div data-testid="appt-success" className="bg-white text-[#0A0A0A] p-10 md:p-14">
              <CheckCircle2 className="w-12 h-12 text-[#EB0A1E] mb-4" />
              <h3 className="font-display font-bold text-3xl md:text-4xl tracking-tight">
                {t("appt.successTitle")}
              </h3>
              <p className="mt-4 text-[#52525B]">
                {t("appt.successBody")}{" "}
                <span className="font-semibold text-[#0A0A0A]">{DEALER.phone}</span>.
              </p>
              <button
                onClick={() => setSuccess(false)}
                data-testid="appt-new"
                className="mt-8 text-sm font-semibold border-b border-[#0A0A0A] pb-1 hover:text-[#EB0A1E] hover:border-[#EB0A1E] transition-colors"
              >
                {t("appt.bookAnother")}
              </button>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              data-testid="appt-form"
              className="bg-white text-[#0A0A0A] p-6 md:p-10 space-y-5"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label={t("appt.name")} id="name">
                  <Input id="name" required data-testid="appt-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="rounded-none border-[#E4E4E7] focus-visible:ring-[#EB0A1E]" />
                </Field>
                <Field label={t("appt.phone")} id="phone">
                  <Input id="phone" required data-testid="appt-phone"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="rounded-none border-[#E4E4E7] focus-visible:ring-[#EB0A1E]" />
                </Field>
              </div>

              <Field label={t("appt.email")} id="email">
                <Input id="email" type="email" required data-testid="appt-email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="rounded-none border-[#E4E4E7] focus-visible:ring-[#EB0A1E]" />
              </Field>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label={t("appt.date")} id="date">
                  <Popover>
                    <PopoverTrigger asChild>
                      <button type="button" data-testid="appt-date-trigger"
                        className="w-full h-10 px-3 border border-[#E4E4E7] rounded-none flex items-center justify-between text-sm hover:border-[#0A0A0A] transition-colors">
                        <span className={date ? "text-[#0A0A0A]" : "text-[#52525B]"}>
                          {date ? format(date, "PPP", { locale: dateLocale }) : t("appt.datePlaceholder")}
                        </span>
                        <CalendarIcon className="w-4 h-4 text-[#52525B]" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 rounded-none" align="start">
                      <Calendar mode="single" selected={date} onSelect={setDate}
                        disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                        locale={dateLocale} initialFocus />
                    </PopoverContent>
                  </Popover>
                </Field>

                <Field label={t("appt.time")} id="time">
                  <Select value={time} onValueChange={setTime}>
                    <SelectTrigger data-testid="appt-time-trigger"
                      className="rounded-none border-[#E4E4E7] focus:ring-[#EB0A1E]">
                      <SelectValue placeholder={t("appt.timePlaceholder")} />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      {TIME_SLOTS.map((tt) => (
                        <SelectItem key={tt} value={tt} data-testid={`appt-time-${tt}`}>{tt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>

              <Field label={t("appt.vehicle")} id="vehicle">
                <Select value={form.vehicle_interest}
                  onValueChange={(v) => setForm({ ...form, vehicle_interest: v })}>
                  <SelectTrigger data-testid="appt-vehicle-trigger"
                    className="rounded-none border-[#E4E4E7] focus:ring-[#EB0A1E]">
                    <SelectValue placeholder={t("appt.vehiclePlaceholder")} />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="Not sure yet">{t("appt.vehicleNotSure")}</SelectItem>
                    {VEHICLES.map((v) => (
                      <SelectItem key={v.id} value={v.name} data-testid={`appt-vehicle-${v.id}`}>
                        Toyota {v.name}
                      </SelectItem>
                    ))}
                    <SelectItem value="Used / Pre-owned">{t("appt.vehicleUsed")}</SelectItem>
                  </SelectContent>
                </Select>
              </Field>

              <Field label={t("appt.message")} id="message">
                <Textarea id="message" rows={4} data-testid="appt-message"
                  placeholder={t("appt.messagePlaceholder")}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="rounded-none border-[#E4E4E7] focus-visible:ring-[#EB0A1E] resize-none" />
              </Field>

              <button type="submit" disabled={loading} data-testid="appt-submit"
                className="w-full bg-[#EB0A1E] hover:bg-[#CC0011] text-white font-semibold py-4 transition-colors disabled:opacity-50">
                {loading ? t("appt.submitting") : t("appt.submit")}
              </button>

              <p className="text-xs text-[#52525B] text-center">{t("appt.disclaimer")}</p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function Field({ label, id, children }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-xs font-bold uppercase tracking-[0.12em] text-[#0A0A0A]">
        {label}
      </Label>
      {children}
    </div>
  );
}

function Bullet({ children }) {
  return (
    <div className="flex items-start gap-3 text-white/85">
      <span className="mt-2 w-1.5 h-1.5 bg-[#EB0A1E] flex-shrink-0" />
      <span>{children}</span>
    </div>
  );
}
